import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Tooltip,
  Typography,
  LinearProgress,
  FormControlLabel,
  Switch,
  Box,
  Divider,
  makeStyles,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import StopIcon from "@material-ui/icons/Stop";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import CallSplitOutlinedIcon from "@material-ui/icons/CallSplitOutlined";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";
import HotelOutlinedIcon from "@material-ui/icons/HotelOutlined";
import { CircularProgress } from "@material-ui/core";
import ScheduleOutlinedIcon from "@material-ui/icons/ScheduleOutlined";
import EventAvailableOutlinedIcon from "@material-ui/icons/EventAvailableOutlined";
import { toast } from "react-toastify";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), margin: theme.spacing(2) },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    alignItems: "center",
  },
  warning: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffe69c",
    color: "#664d03",
    padding: theme.spacing(1.5),
    borderRadius: 6,
    fontSize: 13,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  // Audience builder
  audienceCard: {
    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
    color: "#fff",
    padding: theme.spacing(2),
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 8px rgba(25,118,210,0.2)",
  },
  audienceCount: {
    fontSize: 36,
    fontWeight: 700,
    lineHeight: 1,
  },
  audienceLabel: {
    fontSize: 13,
    opacity: 0.9,
    marginTop: 4,
  },
  audienceSample: {
    fontSize: 12,
    opacity: 0.85,
    marginTop: 6,
    fontStyle: "italic",
  },
  audienceEmpty: {
    background: "#f4f6f9",
    color: "#666",
    border: "1px dashed #ccc",
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  filterTypeChip: {
    minWidth: 140,
  },
  filterValueSelect: {
    flex: 1,
    minWidth: 200,
  },
  filterAddBtn: {
    border: "1px dashed #1976d2",
    borderRadius: 6,
    padding: theme.spacing(1, 2),
    color: "#1976d2",
    width: "100%",
    justifyContent: "center",
  },
}));

/**
 * Formata uma quantidade de minutos em string human-readable:
 *   45  -> "45 min"
 *   90  -> "1h 30min"
 *   600 -> "10h"
 *  1800 -> "1d 6h"
 * Considera dia comercial de 9h pra aproximar o feeling de "vai levar
 * 2 dias e meio de envio", mas o numero base e em minutos corridos —
 * o backend ja respeita business hours no calculo, entao podemos
 * apresentar como "horas decorridas" sem ambiguidade.
 */
const formatDuration = (mins) => {
  const m = Math.max(Math.floor(mins || 0), 0);
  if (m < 1) return "menos de 1 min";
  if (m < 60) return `${m} min`;
  const days = Math.floor(m / (60 * 24));
  const remAfterDays = m - days * 60 * 24;
  const hours = Math.floor(remAfterDays / 60);
  const remMin = remAfterDays - hours * 60;
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (remMin > 0 && days === 0) parts.push(`${remMin}min`);
  return parts.join(" ");
};

const formatFinishDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const isTomorrow =
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate();
  const timeStr = d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (sameDay) return `hoje às ${timeStr}`;
  if (isTomorrow) return `amanhã às ${timeStr}`;
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const FILTER_TYPE_OPTIONS = [
  { value: "all", label: "Todos os clientes que aceitam campanhas", icon: GroupOutlinedIcon, valueMode: "none" },
  { value: "tag", label: "Cliente com tag", icon: LocalOfferOutlinedIcon, valueMode: "tag" },
  { value: "status", label: "Cliente com status", icon: LabelOutlinedIcon, valueMode: "status" },
  { value: "queue", label: "Cliente atendido pela fila", icon: CallSplitOutlinedIcon, valueMode: "queue" },
  { value: "active", label: "Apenas clientes ativos (90d)", icon: PeopleAltOutlinedIcon, valueMode: "none" },
  { value: "inactive", label: "Apenas clientes inativos (90d)", icon: HotelOutlinedIcon, valueMode: "none" },
];

const STATUS_COLORS = {
  draft: "default",
  scheduled: "default",
  running: "primary",
  paused: "default",
  completed: "primary",
  cancelled: "default",
  error: "secondary",
};

const STATUS_LABELS = {
  draft: "Rascunho",
  scheduled: "Agendada",
  running: "Em execução",
  paused: "Pausada",
  completed: "Concluída",
  cancelled: "Cancelada",
  error: "Erro",
};

const SEND_MODES = [
  { value: "business_hours", label: "Horário comercial" },
  { value: "until_done", label: "Até concluir" },
  { value: "scheduled", label: "Agendado" },
];

const emptyForm = {
  name: "",
  whatsappId: "",
  messageType: "text",
  messageText: "Olá {{primeiro_nome}}, ",
  mediaUrl: "",
  mediaCaption: "",
  documentName: "",
  intervalMinSeconds: 60,
  intervalMaxSeconds: 120,
  randomIntervalEnabled: true,
  sendMode: "business_hours",
  startAt: "",
  businessHoursEnabled: true,
  businessStartTime: "09:00:00",
  businessEndTime: "18:00:00",
  filters: [],
};

// FILTER_TYPE_OPTIONS já definido acima — substitui o array legado.

const CampaignsPage = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [rows, setRows] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [dialog, setDialog] = useState({ open: false, form: emptyForm, editing: null });
  const [detail, setDetail] = useState({ open: false, campaign: null, report: null });
  const [confirmStart, setConfirmStart] = useState({ open: false, campaign: null });
  // Audience builder lookups
  const [tags, setTags] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [queues, setQueues] = useState([]);
  const [preview, setPreview] = useState({ count: 0, skippedNoPhone: 0, sample: [], duration: null, loading: false });
  const previewTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/campaigns");
      setRows(data || []);
    } catch {
      toast.error("Erro ao carregar campanhas");
    }
  }, []);

  useEffect(() => {
    api
      .get("/whatsapp")
      .then((r) => setWhatsapps(r.data || []))
      .catch(() => {});
    api
      .get("/client-tags")
      .then((r) => setTags(r.data || []))
      .catch(() => {});
    api
      .get("/client-statuses")
      .then((r) => setStatuses(r.data || []))
      .catch(() => {});
    api
      .get("/queue")
      .then((r) => setQueues(r.data || []))
      .catch(() => {});
    load();
  }, [load]);

  // Preview live: sempre que filters OU parametros de envio mudarem,
  // dispara POST /campaigns/preview-by-filters com debounce de 400ms.
  // O backend recebe `filters` + `intervals` e devolve count + duracao
  // estimada respeitando business_hours.
  const form = dialog.form;
  useEffect(() => {
    if (!dialog.open) {
      setPreview({ count: 0, skippedNoPhone: 0, sample: [], duration: null, loading: false });
      return;
    }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    const filters = form.filters || [];
    if (filters.length === 0) {
      setPreview({ count: 0, skippedNoPhone: 0, sample: [], duration: null, loading: false });
      return;
    }
    setPreview((p) => ({ ...p, loading: true }));
    previewTimer.current = setTimeout(async () => {
      try {
        const payload = {
          filters: filters
            .filter((f) => f.filterType && (f.filterValue || ["all", "active", "inactive"].includes(f.filterType)))
            .map((f) => ({
              filterType: f.filterType,
              filterValue: f.filterValue || "true",
            })),
          intervals: {
            intervalMinSeconds: Number(form.intervalMinSeconds) || 60,
            intervalMaxSeconds: Number(form.intervalMaxSeconds) || 120,
            randomIntervalEnabled: !!form.randomIntervalEnabled,
            sendMode: form.sendMode || "business_hours",
            businessHoursEnabled: !!form.businessHoursEnabled,
            businessStartTime: form.businessStartTime || "09:00:00",
            businessEndTime: form.businessEndTime || "18:00:00",
            startAt: form.startAt || null,
          },
        };
        const { data } = await api.post("/campaigns/preview-by-filters", payload);
        setPreview({
          count: data.count || 0,
          skippedNoPhone: data.skippedNoPhone || 0,
          sample: data.sample || [],
          duration: data.duration || null,
          loading: false,
        });
      } catch {
        setPreview({ count: 0, skippedNoPhone: 0, sample: [], duration: null, loading: false });
      }
    }, 400);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [
    dialog.open,
    form.filters,
    form.intervalMinSeconds,
    form.intervalMaxSeconds,
    form.randomIntervalEnabled,
    form.sendMode,
    form.businessHoursEnabled,
    form.businessStartTime,
    form.businessEndTime,
    form.startAt,
  ]);

  // Polling leve a cada 10s para refletir progresso de campanhas em execucao
  useEffect(() => {
    const hasRunning = rows.some((r) => ["running", "scheduled"].includes(r.status));
    if (!hasRunning) return;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [rows, load]);

  const open = (record = null) => {
    if (record) {
      setDialog({
        open: true,
        editing: record,
        form: {
          ...emptyForm,
          ...record,
          startAt: record.startAt ? record.startAt.slice(0, 16) : "",
          filters: record.filters || [],
        },
      });
    } else {
      setDialog({ open: true, editing: null, form: emptyForm });
    }
  };
  const close = () => setDialog((d) => ({ ...d, open: false }));

  const save = async () => {
    const payload = { ...dialog.form };
    if (!payload.startAt) delete payload.startAt;
    if (!payload.mediaUrl) delete payload.mediaUrl;
    if (!payload.mediaCaption) delete payload.mediaCaption;
    if (!payload.documentName) delete payload.documentName;
    try {
      if (dialog.editing) {
        await api.put(`/campaigns/${dialog.editing.id}`, payload);
        toast.success("Atualizada");
      } else {
        await api.post("/campaigns", payload);
        toast.success("Criada");
      }
      close();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao salvar");
    }
  };

  const remove = async (r) => {
    if (!(await confirm({ title: `Excluir a campanha "${r.name}"?`, message: "A lista de destinatários e o histórico de envio são removidos junto." }))) return;
    try {
      await api.delete(`/campaigns/${r.id}`);
      toast.success("Removida");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao excluir");
    }
  };

  const previewRecipients = async (r) => {
    try {
      const { data } = await api.get(`/campaigns/${r.id}/preview-recipients`);
      toast.info(
        `Preview: ${data.count} destinatários (${data.skippedNoPhone} sem telefone)`
      );
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro no preview");
    }
  };

  const generateRecipients = async (r) => {
    if (!(await confirm({ title: "Gerar lista de destinatários?", message: "A lista atual é substituída pelo resultado dos filtros atuais." }))) return;
    try {
      const { data } = await api.post(`/campaigns/${r.id}/generate-recipients`);
      toast.success(`${data.count} destinatários gerados`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao gerar");
    }
  };

  const askStart = (r) => setConfirmStart({ open: true, campaign: r });
  const doStart = async () => {
    const r = confirmStart.campaign;
    if (!r) return;
    try {
      await api.post(`/campaigns/${r.id}/start`, { riskAcknowledged: true });
      toast.success("Campanha iniciada");
      setConfirmStart({ open: false, campaign: null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao iniciar");
    }
  };

  const pause = async (r) => {
    try {
      await api.post(`/campaigns/${r.id}/pause`);
      toast.success("Pausada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao pausar");
    }
  };

  const resume = async (r) => {
    try {
      await api.post(`/campaigns/${r.id}/resume`);
      toast.success("Retomada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao retomar");
    }
  };

  const cancel = async (r) => {
    if (!(await confirm({ title: "Cancelar a campanha?", message: "Os envios pendentes são descartados e a campanha não pode ser retomada." }))) return;
    try {
      await api.post(`/campaigns/${r.id}/cancel`);
      toast.success("Cancelada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao cancelar");
    }
  };

  const showDetail = async (r) => {
    try {
      const { data } = await api.get(`/campaigns/${r.id}/report`);
      setDetail({ open: true, campaign: r, report: data });
    } catch {
      toast.error("Erro ao carregar relatório");
    }
  };

  const addFilter = (type = "all") => {
    setDialog((d) => ({
      ...d,
      form: {
        ...d.form,
        filters: [
          ...d.form.filters,
          { filterType: type, filterValue: ["all", "active", "inactive"].includes(type) ? "true" : "" },
        ],
      },
    }));
  };

  const removeFilter = (idx) => {
    setDialog((d) => ({
      ...d,
      form: {
        ...d.form,
        filters: d.form.filters.filter((_, i) => i !== idx),
      },
    }));
  };

  const setFilter = (idx, key, value) => {
    setDialog((d) => ({
      ...d,
      form: {
        ...d.form,
        filters: d.form.filters.map((f, i) => {
          if (i !== idx) return f;
          const next = { ...f, [key]: value };
          // Quando muda o tipo, reseta o filterValue para o default do
          // novo tipo (true para flags binarias, "" para selects)
          if (key === "filterType") {
            next.filterValue = ["all", "active", "inactive"].includes(value) ? "true" : "";
          }
          return next;
        }),
      },
    }));
  };

  // Opcoes do select de valor por filterType
  const valueOptionsFor = useCallback(
    (filterType) => {
      if (filterType === "tag") return tags.map((t) => ({ value: String(t.id), label: t.name }));
      if (filterType === "status") return statuses.map((s) => ({ value: String(s.id), label: s.name }));
      if (filterType === "queue") return queues.map((q) => ({ value: String(q.id), label: q.name }));
      return null; // tipos que nao precisam de valor (all/active/inactive)
    },
    [tags, statuses, queues]
  );

  const progressPercent = (r) => {
    if (!r.totalRecipients) return 0;
    return Math.round(
      ((r.totalSent + r.totalErrors) / r.totalRecipients) * 100
    );
  };

  return (
    <Paper className={classes.root}>
      {confirmDialog}
      <h2>Campanhas de WhatsApp</h2>
      <div className={classes.warning}>
        Envios em massa por WhatsApp têm risco de bloqueio do número. Envie
        apenas para clientes que autorizaram receber comunicações e respeite
        intervalos razoáveis.
      </div>
      <div className={classes.filters}>
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => open(null)}
        >
          Nova campanha
        </Button>
        <IconButton onClick={load}>
          <RefreshIcon />
        </IconButton>
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Nome</TableCell>
            <TableCell>Conexão</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Progresso</TableCell>
            <TableCell>Risco</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>
                <Box>
                  <strong>{r.name}</strong>
                  <Typography variant="caption" display="block" color="textSecondary">
                    {r.messageType}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                {r.whatsapp?.name || whatsapps.find((w) => w.id === r.whatsappId)?.name || "—"}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={STATUS_LABELS[r.status] || r.status}
                  color={STATUS_COLORS[r.status] || "default"}
                />
              </TableCell>
              <TableCell style={{ minWidth: 200 }}>
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent(r)}
                    style={{ flex: 1 }}
                  />
                  <Typography variant="caption">
                    {r.totalSent}/{r.totalRecipients}
                  </Typography>
                </Box>
                {r.totalErrors > 0 && (
                  <Typography variant="caption" color="error">
                    {r.totalErrors} erro(s)
                  </Typography>
                )}
                {/* "Enviadas" só diz que a mensagem saiu. Entregue, lida e
                    respondida são o que dizem se a campanha funcionou — e
                    resposta é a única que vira dinheiro, por isso em destaque. */}
                {r.totalSent > 0 && (
                  <Box display="flex" style={{ gap: 8, marginTop: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                      {r.totalDelivered || 0} entregues
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {r.totalRead || 0} lidas
                    </Typography>
                    <Typography
                      variant="caption"
                      style={{
                        color: r.totalReplied > 0 ? "#0D9488" : undefined,
                        fontWeight: r.totalReplied > 0 ? 600 : undefined,
                      }}
                    >
                      {r.totalReplied || 0} responderam
                    </Typography>
                  </Box>
                )}
                {["running", "scheduled", "paused"].includes(r.status) && r.remainingMinutes != null && (
                  <Box display="flex" alignItems="center" style={{ gap: 4, marginTop: 2 }}>
                    <ScheduleOutlinedIcon style={{ fontSize: 13, color: "#666" }} />
                    <Typography variant="caption" color="textSecondary">
                      Resta {formatDuration(r.remainingMinutes)} · conclui {formatFinishDate(r.estimatedFinishAt)}
                    </Typography>
                  </Box>
                )}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={r.riskLevel}
                  style={{
                    backgroundColor:
                      r.riskLevel === "high"
                        ? "#ffcdd2"
                        : r.riskLevel === "medium"
                        ? "#fff3cd"
                        : "#c8e6c9",
                  }}
                />
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Detalhes/Relatório">
                  <IconButton size="small" onClick={() => showDetail(r)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {["draft", "scheduled", "paused"].includes(r.status) && (
                  <>
                    <Tooltip title="Preview destinatários">
                      <IconButton size="small" onClick={() => previewRecipients(r)}>
                        🔍
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Gerar lista">
                      <IconButton size="small" onClick={() => generateRecipients(r)}>
                        📋
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Iniciar campanha">
                      <IconButton size="small" onClick={() => askStart(r)}>
                        <PlayArrowIcon fontSize="small" style={{ color: "#2e7d32" }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => open(r)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
                {r.status === "running" && (
                  <Tooltip title="Pausar">
                    <IconButton size="small" onClick={() => pause(r)}>
                      <PauseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {["running", "paused", "scheduled"].includes(r.status) && (
                  <Tooltip title="Cancelar">
                    <IconButton size="small" onClick={() => cancel(r)}>
                      <StopIcon fontSize="small" style={{ color: "#d32f2f" }} />
                    </IconButton>
                  </Tooltip>
                )}
                {r.status === "paused" && (
                  <Tooltip title="Retomar">
                    <IconButton size="small" onClick={() => resume(r)}>
                      <PlayArrowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {["draft", "cancelled", "completed", "error"].includes(r.status) && (
                  <IconButton size="small" onClick={() => remove(r)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                Nenhuma campanha. Clique em "Nova campanha".
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Modal criar/editar */}
      <Dialog open={dialog.open} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialog.editing ? "Editar campanha" : "Nova campanha"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="Nome *"
                value={dialog.form.name}
                onChange={(e) =>
                  setDialog({
                    ...dialog,
                    form: { ...dialog.form, name: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                size="small"
                variant="outlined"
                label="Conexão WhatsApp *"
                value={dialog.form.whatsappId || ""}
                onChange={(e) =>
                  setDialog({
                    ...dialog,
                    form: { ...dialog.form, whatsappId: e.target.value },
                  })
                }
              >
                {whatsapps.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name} ({w.status})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                size="small"
                variant="outlined"
                label="Tipo de mensagem"
                value={dialog.form.messageType}
                onChange={(e) =>
                  setDialog({
                    ...dialog,
                    form: { ...dialog.form, messageType: e.target.value },
                  })
                }
              >
                <MenuItem value="text">Texto</MenuItem>
                <MenuItem value="image">Imagem</MenuItem>
                <MenuItem value="video">Vídeo</MenuItem>
                <MenuItem value="audio">Áudio</MenuItem>
                <MenuItem value="document">Documento</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                size="small"
                variant="outlined"
                label="Modo de envio"
                value={dialog.form.sendMode}
                onChange={(e) =>
                  setDialog({
                    ...dialog,
                    form: { ...dialog.form, sendMode: e.target.value },
                  })
                }
              >
                {SEND_MODES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="datetime-local"
                size="small"
                variant="outlined"
                label="Início (agendado)"
                InputLabelProps={{ shrink: true }}
                value={dialog.form.startAt || ""}
                onChange={(e) =>
                  setDialog({
                    ...dialog,
                    form: { ...dialog.form, startAt: e.target.value },
                  })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                variant="outlined"
                label="Mensagem (suporta {{nome}}, {{primeiro_nome}}, {{empresa}}, {{telefone}}, {{email}}, {{status_cliente}})"
                value={dialog.form.messageText || ""}
                onChange={(e) =>
                  setDialog({
                    ...dialog,
                    form: { ...dialog.form, messageText: e.target.value },
                  })
                }
              />
            </Grid>

            {dialog.form.messageType !== "text" && (
              <>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="URL da mídia"
                    value={dialog.form.mediaUrl || ""}
                    onChange={(e) =>
                      setDialog({
                        ...dialog,
                        form: { ...dialog.form, mediaUrl: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Nome do documento"
                    value={dialog.form.documentName || ""}
                    onChange={(e) =>
                      setDialog({
                        ...dialog,
                        form: { ...dialog.form, documentName: e.target.value },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    variant="outlined"
                    label="Legenda da mídia"
                    value={dialog.form.mediaCaption || ""}
                    onChange={(e) =>
                      setDialog({
                        ...dialog,
                        form: { ...dialog.form, mediaCaption: e.target.value },
                      })
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                size="small"
                variant="outlined"
                label="Intervalo mín (s)"
                value={dialog.form.intervalMinSeconds}
                onChange={(e) =>
                  setDialog({
                    ...dialog,
                    form: {
                      ...dialog.form,
                      intervalMinSeconds: Number(e.target.value) || 30,
                    },
                  })
                }
                inputProps={{ min: 30, step: 10 }}
                helperText="Mínimo: 30s"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                size="small"
                variant="outlined"
                label="Intervalo máx (s)"
                value={dialog.form.intervalMaxSeconds}
                onChange={(e) =>
                  setDialog({
                    ...dialog,
                    form: {
                      ...dialog.form,
                      intervalMaxSeconds: Number(e.target.value) || 60,
                    },
                  })
                }
                inputProps={{ min: 30, step: 10 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!dialog.form.randomIntervalEnabled}
                    onChange={(e) =>
                      setDialog({
                        ...dialog,
                        form: {
                          ...dialog.form,
                          randomIntervalEnabled: e.target.checked,
                        },
                      })
                    }
                  />
                }
                label="Aleatório"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!dialog.form.businessHoursEnabled}
                    onChange={(e) =>
                      setDialog({
                        ...dialog,
                        form: {
                          ...dialog.form,
                          businessHoursEnabled: e.target.checked,
                        },
                      })
                    }
                  />
                }
                label="Horário comercial"
              />
            </Grid>

            {dialog.form.businessHoursEnabled && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="time"
                    size="small"
                    variant="outlined"
                    label="Início"
                    value={dialog.form.businessStartTime?.slice(0, 5) || "09:00"}
                    onChange={(e) =>
                      setDialog({
                        ...dialog,
                        form: {
                          ...dialog.form,
                          businessStartTime: `${e.target.value}:00`,
                        },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="time"
                    size="small"
                    variant="outlined"
                    label="Fim"
                    value={dialog.form.businessEndTime?.slice(0, 5) || "18:00"}
                    onChange={(e) =>
                      setDialog({
                        ...dialog,
                        form: {
                          ...dialog.form,
                          businessEndTime: `${e.target.value}:00`,
                        },
                      })
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider style={{ marginBottom: 12 }} />
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <PeopleAltOutlinedIcon color="primary" />
                <Typography variant="subtitle2">Público-alvo</Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Monte a audiência combinando filtros. A contagem é
                atualizada em tempo real — o sistema sempre exclui
                clientes que descadastraram ou foram bloqueados para
                campanhas.
              </Typography>
            </Grid>

            {/* Card de preview live */}
            <Grid item xs={12}>
              <Box
                className={`${classes.audienceCard} ${
                  preview.count === 0 && !preview.loading ? classes.audienceEmpty : ""
                }`}
              >
                <PeopleAltOutlinedIcon style={{ fontSize: 36, opacity: 0.85 }} />
                <Box flex={1}>
                  <Box display="flex" alignItems="baseline" style={{ gap: 8 }}>
                    {preview.loading ? (
                      <CircularProgress size={20} style={{ color: "inherit" }} />
                    ) : (
                      <Typography className={classes.audienceCount}>
                        {preview.count.toLocaleString("pt-BR")}
                      </Typography>
                    )}
                    <Typography className={classes.audienceLabel}>
                      {preview.count === 1 ? "destinatário receberá" : "destinatários receberão"} a mensagem
                    </Typography>
                  </Box>
                  {preview.skippedNoPhone > 0 && (
                    <Typography className={classes.audienceLabel}>
                      {preview.skippedNoPhone} cliente(s) ignorado(s) por estarem sem telefone cadastrado
                    </Typography>
                  )}
                  {preview.sample.length > 0 && (
                    <Typography className={classes.audienceSample}>
                      Exemplos: {preview.sample.map((s) => s.name).join(", ")}
                      {preview.count > preview.sample.length ? "…" : ""}
                    </Typography>
                  )}
                  {preview.count === 0 && !preview.loading && (
                    <Typography className={classes.audienceLabel}>
                      Adicione pelo menos um filtro abaixo para selecionar o público.
                    </Typography>
                  )}
                </Box>
                {preview.duration && preview.count > 0 && (
                  <Box
                    style={{
                      borderLeft: "1px solid rgba(255,255,255,0.3)",
                      paddingLeft: 16,
                      minWidth: 200,
                    }}
                  >
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      <ScheduleOutlinedIcon style={{ fontSize: 18, opacity: 0.85 }} />
                      <Typography style={{ fontSize: 13, opacity: 0.9 }}>
                        Duração estimada
                      </Typography>
                    </Box>
                    <Typography style={{ fontSize: 20, fontWeight: 600 }}>
                      {formatDuration(preview.duration.durationMinutes)}
                    </Typography>
                    <Box display="flex" alignItems="center" style={{ gap: 6, marginTop: 4 }}>
                      <EventAvailableOutlinedIcon style={{ fontSize: 16, opacity: 0.8 }} />
                      <Typography style={{ fontSize: 12, opacity: 0.9 }}>
                        Conclui {formatFinishDate(preview.duration.estimatedFinishAt)}
                      </Typography>
                    </Box>
                    {preview.duration.perDayCapacity && form.businessHoursEnabled && (
                      <Typography style={{ fontSize: 11, opacity: 0.8, marginTop: 4 }}>
                        ~{preview.duration.perDayCapacity.toLocaleString("pt-BR")} envios por dia comercial
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Lista de filtros */}
            {dialog.form.filters.map((f, idx) => {
              const typeOption = FILTER_TYPE_OPTIONS.find((o) => o.value === f.filterType) || FILTER_TYPE_OPTIONS[0];
              const valueOpts = valueOptionsFor(f.filterType);
              const Icon = typeOption.icon;
              return (
                <Grid item xs={12} key={idx}>
                  <Box className={classes.filterRow}>
                    <Icon style={{ color: "#1976d2" }} />
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      value={f.filterType}
                      onChange={(e) => setFilter(idx, "filterType", e.target.value)}
                      className={classes.filterTypeChip}
                    >
                      {FILTER_TYPE_OPTIONS.map((ft) => (
                        <MenuItem key={ft.value} value={ft.value}>
                          {ft.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {valueOpts ? (
                      <TextField
                        select
                        size="small"
                        variant="outlined"
                        value={f.filterValue || ""}
                        onChange={(e) => setFilter(idx, "filterValue", e.target.value)}
                        className={classes.filterValueSelect}
                        placeholder="Selecione…"
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="" disabled>
                          {valueOpts.length === 0
                            ? f.filterType === "tag"
                              ? "Nenhuma tag cadastrada"
                              : f.filterType === "status"
                              ? "Nenhum status cadastrado"
                              : "Nenhuma fila cadastrada"
                            : "Selecione…"}
                        </MenuItem>
                        {valueOpts.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <Box flex={1}>
                        <Chip
                          size="small"
                          label="Aplicado a toda base elegível"
                          style={{ backgroundColor: "#e3f2fd", color: "#1565c0" }}
                        />
                      </Box>
                    )}
                    <IconButton size="small" onClick={() => removeFilter(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              );
            })}

            {/* Atalhos rápidos para adicionar tipos comuns */}
            <Grid item xs={12}>
              <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  startIcon={<LocalOfferOutlinedIcon />}
                  onClick={() => addFilter("tag")}
                  variant="outlined"
                >
                  Adicionar por tag
                </Button>
                <Button
                  size="small"
                  startIcon={<LabelOutlinedIcon />}
                  onClick={() => addFilter("status")}
                  variant="outlined"
                >
                  Adicionar por status
                </Button>
                <Button
                  size="small"
                  startIcon={<CallSplitOutlinedIcon />}
                  onClick={() => addFilter("queue")}
                  variant="outlined"
                >
                  Adicionar por fila
                </Button>
                <Button
                  size="small"
                  startIcon={<GroupOutlinedIcon />}
                  onClick={() => addFilter("all")}
                  variant="outlined"
                >
                  Toda a base
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={save}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de detalhes/relatório */}
      <Dialog
        open={detail.open}
        onClose={() => setDetail({ open: false, campaign: null, report: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Relatório: {detail.campaign?.name}</DialogTitle>
        <DialogContent>
          {detail.report && (
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Status</Typography>
                <Typography>
                  <Chip
                    size="small"
                    label={STATUS_LABELS[detail.report.status] || detail.report.status}
                    color={STATUS_COLORS[detail.report.status] || "default"}
                  />
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Progresso</Typography>
                <Typography>{detail.report.percentDone}%</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Risco</Typography>
                <Typography>{detail.report.riskLevel}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Intervalo médio</Typography>
                <Typography>
                  {detail.report.avgIntervalSeconds
                    ? `${detail.report.avgIntervalSeconds}s`
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Total destinatários</Typography>
                <Typography>{detail.report.counters?.total || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Enviados</Typography>
                <Typography>{detail.report.counters?.sent || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Pendentes</Typography>
                <Typography>{detail.report.counters?.pending || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Erros</Typography>
                <Typography style={{ color: "#d32f2f" }}>
                  {detail.report.counters?.errors || 0}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Entregues</Typography>
                <Typography>{detail.report.counters?.delivered || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Lidas</Typography>
                <Typography>{detail.report.counters?.read || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Respondidas</Typography>
                <Typography>{detail.report.counters?.replied || 0}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Início</Typography>
                <Typography>
                  {detail.report.startedAt
                    ? new Date(detail.report.startedAt).toLocaleString("pt-BR")
                    : "—"}
                </Typography>
              </Grid>

              {/* Tempo previsto / restante */}
              <Grid item xs={12}>
                <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <ScheduleOutlinedIcon color="primary" />
                  <Typography variant="subtitle2">Tempo de execução</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Decorrido</Typography>
                <Typography>
                  {detail.report.timing?.elapsedMinutes != null
                    ? formatDuration(detail.report.timing.elapsedMinutes)
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Restante estimado</Typography>
                <Typography style={{ fontWeight: 600 }}>
                  {detail.report.timing?.remainingMinutes != null
                    ? formatDuration(detail.report.timing.remainingMinutes)
                    : detail.report.status === "completed"
                    ? "concluída"
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Conclusão prevista</Typography>
                <Typography style={{ fontWeight: 600, color: "#1976d2" }}>
                  {detail.report.status === "completed" && detail.report.finishedAt
                    ? new Date(detail.report.finishedAt).toLocaleString("pt-BR")
                    : formatFinishDate(detail.report.timing?.estimatedFinishAt)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption">Capacidade / dia comercial</Typography>
                <Typography>
                  {detail.report.timing?.perDayCapacity
                    ? `${detail.report.timing.perDayCapacity.toLocaleString("pt-BR")} envios`
                    : "tempo corrido"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail({ open: false, campaign: null, report: null })}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação de início (aviso de risco obrigatório) */}
      <Dialog
        open={confirmStart.open}
        onClose={() => setConfirmStart({ open: false, campaign: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Iniciar campanha</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            Você está prestes a iniciar a campanha{" "}
            <strong>{confirmStart.campaign?.name}</strong>.
          </Typography>
          <div className={classes.warning}>
            Certifique-se de que os contatos selecionados autorizaram o
            recebimento dessas mensagens. Envios em massa podem gerar denúncias,
            bloqueios ou banimento do número caso sejam feitos de forma abusiva.
          </div>
          <Typography variant="caption">
            Ao confirmar, você reconhece o risco e autoriza o início do envio.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStart({ open: false, campaign: null })}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={doStart}>
            Confirmar e iniciar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CampaignsPage;
